import { Problem, routes, type RouteName, type Routes } from "@malfak/contracts";
import type { z } from "zod";
import { config } from "../config";
import { uuid } from "../utils";
import { ApiError, NO_ORGANIZATION, NO_ORGANIZATION_TYPE } from "./errors";

/**
 * A fully typed client derived from the `routes` contract (packages/contracts): no
 * hand-written endpoints, no codegen drift. `api("inbox", { query: { state: "open" } })`
 * gets its path, method, param/query/body types and response type from the route.
 *
 * `orgId` is filled in from the current org when omitted, so feature code rarely names it.
 */

type Def<K extends RouteName> = Routes[K];
type IsUnset<T> = [z.ZodTypeAny] extends [T] ? true : false;
type Schema<K extends RouteName, F extends "params" | "query" | "body"> = Def<K> extends { [P in F]?: infer S } ? NonNullable<S> : never;

type ParamsIn<K extends RouteName> = IsUnset<Schema<K, "params">> extends true
  ? never
  : Schema<K, "params"> extends z.ZodTypeAny
    ? Omit<z.input<Schema<K, "params">>, "orgId"> & { orgId?: string }
    : never;
type QueryIn<K extends RouteName> = IsUnset<Schema<K, "query">> extends true ? never : Schema<K, "query"> extends z.ZodTypeAny ? Partial<z.input<Schema<K, "query">>> : never;
type BodyIn<K extends RouteName> = IsUnset<Schema<K, "body">> extends true ? never : Schema<K, "body"> extends z.ZodTypeAny ? z.input<Schema<K, "body">> : never;

type ParamsArg<K extends RouteName> = [ParamsIn<K>] extends [never]
  ? { params?: undefined }
  : keyof Omit<ParamsIn<K>, "orgId"> extends never
    ? { params?: ParamsIn<K> }
    : { params: ParamsIn<K> };
type QueryArg<K extends RouteName> = [QueryIn<K>] extends [never] ? { query?: undefined } : { query?: QueryIn<K> };
type BodyArg<K extends RouteName> = [BodyIn<K>] extends [never] ? { body?: undefined } : { body: BodyIn<K> };

export type ApiArgs<K extends RouteName> = ParamsArg<K> & QueryArg<K> & BodyArg<K> & { signal?: AbortSignal; headers?: Record<string, string> };
export type ApiResponse<K extends RouteName> = z.output<Def<K>["response"]>;
export type ApiBody<K extends RouteName> = BodyIn<K>;
export type ApiQuery<K extends RouteName> = QueryIn<K>;

/** Routes that may be called with no arguments at all. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- "{} extends X" asks whether every field of X is optional
type NoRequired<K extends RouteName> = {} extends ApiArgs<K> ? true : false;
export type ApiCallArgs<K extends RouteName> = NoRequired<K> extends true ? [args?: ApiArgs<K>] : [args: ApiArgs<K>];

// ---------------------------------------------------------------- org context
let currentOrgId: string | null = null;
export const setCurrentOrgId = (id: string | null) => {
  currentOrgId = id;
};
export const getCurrentOrgId = () => currentOrgId;

/** Fired when a non-auth call returns 401: the session ended elsewhere. */
export const AUTH_LOST_EVENT = "malfak:auth-lost";

export function buildPath(path: string, params: Record<string, unknown> = {}): string {
  return path.replace(/:([A-Za-z_]+)/g, (_, key: string) => {
    const v = params[key];
    if (v === undefined || v === null || v === "") throw new Error(`Missing path param "${key}" for ${path}`);
    return encodeURIComponent(String(v));
  });
}

export function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function routeUrl<K extends RouteName>(name: K, args?: { params?: Record<string, unknown>; query?: Record<string, unknown> }) {
  const def = routes[name];
  const params = { orgId: currentOrgId ?? undefined, ...(args?.params ?? {}) };
  return `${config().apiBaseUrl}${buildPath(def.path, params)}${buildQuery(args?.query)}`;
}

const AUTH_ROUTES = new Set<RouteName>(["me", "session", "login", "signup", "logout", "requestAccess", "usernameAvailable"]);

export async function api<K extends RouteName>(name: K, ...rest: ApiCallArgs<K>): Promise<ApiResponse<K>> {
  const args = (rest[0] ?? {}) as { params?: Record<string, unknown>; query?: Record<string, unknown>; body?: unknown; signal?: AbortSignal; headers?: Record<string, string> };
  const def = routes[name];
  const requestId = uuid();
  // an org-scoped route without a current org (a login with no organization): fail clearly, never build "/v1/orgs//…"
  if (def.path.includes(":orgId") && !(args.params?.orgId ?? currentOrgId)) {
    throw new ApiError({ status: 409, route: name, requestId, problem: { type: NO_ORGANIZATION_TYPE, title: NO_ORGANIZATION, status: 409, detail: "You are not part of an organization yet: create one to continue" } });
  }
  const url = routeUrl(name, args);
  const headers: Record<string, string> = { Accept: "application/json", "x-request-id": requestId, ...(args.headers ?? {}) };
  let body: BodyInit | undefined;
  if (def.method !== "GET" && def.method !== "DELETE") {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(args.body ?? {});
  }

  let res: Response;
  try {
    res = await fetch(url, { method: def.method, headers, body, credentials: "include", signal: args.signal });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError({ status: 0, route: name, requestId, message: "Network unreachable" });
  }
  const rid = res.headers.get("x-request-id") ?? requestId;

  if (!res.ok) {
    let problem: z.infer<typeof Problem>;
    try {
      const raw: unknown = await res.json();
      const parsed = Problem.safeParse(raw);
      problem = parsed.success ? parsed.data : { type: "about:blank", title: res.statusText || "Error", status: res.status, detail: typeof raw === "object" && raw && "message" in raw ? String((raw as { message: unknown }).message) : undefined };
    } catch {
      problem = { type: "about:blank", title: res.statusText || "Error", status: res.status };
    }
    if (res.status === 401 && !AUTH_ROUTES.has(name) && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(AUTH_LOST_EVENT));
    }
    throw new ApiError({ status: res.status, problem, requestId: rid, route: name });
  }

  if (res.status === 204) return undefined as ApiResponse<K>;
  const json: unknown = await res.json();
  // Validate against the contract: defaults are applied, and drift is loud in dev.
  const parsed = def.response.safeParse(json);
  if (!parsed.success) {
    if (import.meta.env.DEV) console.warn(`[api] ${name}: response does not match the contract`, parsed.error.issues.slice(0, 5));
    return json as ApiResponse<K>;
  }
  return parsed.data as ApiResponse<K>;
}
